import { useState } from "react";
import { Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errorText } from "@/lib/api";

const nowLocal = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 19);
};

const empty = () => ({ identity: "", arrived_at: nowLocal(), site: "", user_agent: "" });

export const ManualEntryDialog = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/entries", { ...form, arrived_at: new Date(form.arrived_at).toISOString() });
      toast.success(`Entry #${data.seq} added to the ledger`);
      setOpen(false);
      setForm(empty());
      onCreated();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setForm(empty()); }}>
      <DialogTrigger asChild>
        <Button className="bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold" data-testid="manual-entry-modal-open-btn">
          <Plus className="h-4 w-4 mr-2" />Log arrival
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0F141C] border-[#222D3D]" data-testid="manual-entry-modal">
        <DialogHeader>
          <DialogTitle className="font-display">Log an arrival by hand</DialogTitle>
          <DialogDescription>Once added, an entry is chained and cannot be edited.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Agent identity" required value={form.identity} onChange={set("identity")} placeholder="e.g. GPTBot, ClaudeBot" testId="manual-entry-agent-name-input" />
          <Field label="Arrival time" type="datetime-local" step="1" required value={form.arrived_at} onChange={set("arrived_at")} testId="manual-entry-timestamp-input" />
          <Field label="Site or page (optional)" value={form.site} onChange={set("site")} placeholder="doorman.com/pricing" testId="manual-entry-origin-input" />
          <Field label="User-agent string (optional)" value={form.user_agent} onChange={set("user_agent")} placeholder="Mozilla/5.0 (compatible; GPTBot/1.2)" testId="manual-entry-ua-input" />
          <Button type="submit" disabled={busy} className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold" data-testid="manual-entry-submit-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add to ledger"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, testId, ...props }) => (
  <div className="space-y-2">
    <Label>{label}</Label>
    <Input {...props} data-testid={testId} className="bg-[#07090E]" />
  </div>
);
